import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Database, CheckCircle2, XCircle } from 'lucide-react';

export function DatabaseHealthCard() {
  const { data: healthData, isLoading } = useQuery({
    queryKey: ['database-health'],
    queryFn: async () => {
      const estimate = (table: 'companies' | 'profiles' | 'leads' | 'jobs' | 'invites') =>
        supabase.from(table).select('id', { count: 'estimated', head: true });

      const [companies, profiles, leads, jobs, invites] = await Promise.all([
        estimate('companies'),
        estimate('profiles'),
        estimate('leads'),
        estimate('jobs'),
        estimate('invites'),
      ]);

      return {
        companies: companies.count || 0,
        profiles: profiles.count || 0,
        leads: leads.count || 0,
        jobs: jobs.count || 0,
        invites: invites.count || 0,
        isHealthy: ![companies, profiles, leads, jobs, invites].some((r) => r.error),
      };
    },
    staleTime: 5 * 60_000,
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Database Health
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Database Health
          </CardTitle>
          {healthData?.isHealthy ? (
            <Badge variant="success">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Healthy
            </Badge>
          ) : (
            <Badge variant="destructive">
              <XCircle className="h-3 w-3 mr-1" />
              Issues Detected
            </Badge>
          )}
        </div>
        <CardDescription>Database statistics and connection status</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Companies</p>
              <p className="text-2xl font-bold">{healthData?.companies}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Users</p>
              <p className="text-2xl font-bold">{healthData?.profiles}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Leads</p>
              <p className="text-2xl font-bold">{healthData?.leads}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Jobs</p>
              <p className="text-2xl font-bold">{healthData?.jobs}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Pending Invites</p>
              <p className="text-2xl font-bold">{healthData?.invites}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
